import { FileDownloader } from "./file-download";
import HTMLAst from "html-parse-stringify";

export class RestApi {
  constructor(private baseUri: string, private getAccessToken: () => Promise<string>) {}

  async uploadImage(file: File): Promise<string> {
    const fileType = file.type;
    const imageBuffer = await file.arrayBuffer();

    const uint8Array = new Uint8Array(imageBuffer);

    const accessToken = await this.getAccessToken();
    const response = await fetch(`${this.baseUri}/proposal/header-footer`, {
      method: "POST",
      body: uint8Array,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": fileType,
      },
    });

    return response.json();
  }

  async getProposalDocumentVersion(matterVersionId: number): Promise<FileDownloader> {
    const response = await this.get(`matter/version/${matterVersionId}/proposal;document=true`);
    return new FileDownloader(response, "Virtual Pricing Director Proposal.docx");
  }

  async getCurrentProposalDocumentVersion(matterId: number): Promise<FileDownloader> {
    const response = await this.get(`matter/${matterId}/proposal;document=true`);
    return new FileDownloader(response, "Virtual Pricing Director Proposal.docx");
  }
  async getCurrentParentProposalDocumentVersion(parentId: number): Promise<FileDownloader> {
    const response = await this.get(`parent-matter/${parentId}/proposal;document=true`);
    return new FileDownloader(response, "Virtual Pricing Director Proposal.docx");
  }

  async getWorkplanSpreadsheet(matterId: number): Promise<FileDownloader> {
    const response = await this.get(`matter/${matterId}/workplan`);
    return new FileDownloader(response, "Virtual Pricing Director – Workplan.xlsx");
  }

  async getTemplate(matterTemplateId: number): Promise<FileDownloader> {
    const response = await this.get(`matter/${matterTemplateId}/proposal;document=true`);
    return new FileDownloader(response, "Virtual Pricing Director Proposal.docx");
  }

  async downloadUsageReport(): Promise<FileDownloader> {
    const response = await this.get(`usage-report`);
    return new FileDownloader(response, "Usage Report.xlsx");
  }

  async downloadAssumptionsExclusionsExport(): Promise<FileDownloader> {
    const response = await this.get(`assumptions-exclusions-export`);
    return new FileDownloader(response, "Assumptions Exclusions Export.xlsx");
  }

  async generateTemplateFromHtml(htmlString: string): Promise<FileDownloader> {
    const accessToken = await this.getAccessToken();
    const response = await fetch(`${this.baseUri}/convert-html-doc`, {
      method: "POST",
      body: htmlString,
      headers: {
        "Content-Type": "text/html",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return new FileDownloader(response, "test123.docx");
  }


  async downloadProposalPdf(htmlContent: string, matterId: number, selectedDesignId: number) {
    const accessToken = await this.getAccessToken();
    const toAst = HTMLAst.parse(htmlContent);
    const response = await fetch(
      `${this.baseUri}/proposal-pdf/${matterId}?designId=${selectedDesignId}`,
      {
        method: "POST",
        body: JSON.stringify(toAst),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return new FileDownloader(response, "Virtual Pricing Director Proposal.pdf");
  }

  async downloadClientReportPdf(htmlContent: string, matterId: number) {
    const safeHtmlContent = htmlContent.replace(/\n/g, " ").replace(/<!--[\s\S]*?-->/g, "");
    const accessToken = await this.getAccessToken();
    const toAst = HTMLAst.parse(safeHtmlContent);
    const response = await fetch(`${this.baseUri}/client-report-pdf/${matterId}`, {
      method: "POST",
      body: JSON.stringify(toAst),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return new FileDownloader(response, "Virtual Pricing Director Client Report.pdf");
  }

  async downloadProposalByMatterId<T extends boolean>(
    matterId: number,
    asHtml: T
  ): Promise<T extends true ? string : FileDownloader> {
    const accessToken = await this.getAccessToken();
    let url = `${this.baseUri}/download-proposal/${matterId}`;

    if (asHtml) {
      url += "?format=html";
    }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (asHtml) {
      return response.text() as any;
    }

    return new FileDownloader(response, "test123.docx") as any;
  }

  async downloadAllTemplates(): Promise<FileDownloader> {
    const accessToken = await this.getAccessToken();

    const response = await fetch(`${this.baseUri}/templates/all`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return new FileDownloader(response, "Virtual Pricing Director Templates.zip");
  }

  async get(uri: string): Promise<Response> {
    const accessToken = await this.getAccessToken();
    return fetch(`${this.baseUri}/${uri}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }

  async downloadReport(htmlContent: string) {
    const accessToken = await this.getAccessToken();
    const toAst = HTMLAst.parse(htmlContent);
    const response = await fetch(`${this.baseUri}/report`, {
      method: "POST",
      body: JSON.stringify(toAst),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return new FileDownloader(response, "report-test.pdf");
  }

  async getOpenAICompletion(
    messages: Array<{ role: "system" | "user"; content: string }>
  ): Promise<any> {
    const accessToken = await this.getAccessToken();

    const response = await fetch(`${this.baseUri}/openai/prompt`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ messages }),
    });

    if (response.ok) {
      return response.json();
    } else {
      throw new Error(`Failed to get completion from OpenAI: ${response.statusText}`);
    }
  }
}
